import {
  childrenEdgesAtomFamily,
  edgesAtom,
  nodeAtomFamily,
  nodesAtom,
  PathNodeData,
  saveCanvasSetter,
} from "@zapier/canvas-state";
import { useAtomValue, useSetAtom } from "jotai";
import { useCallback, useRef } from "react";
import { Edge, Node } from "@xyflow/react";
import { Button, Spacer, Text, TextInput } from "@zapier/design-system";

import { Sidebar } from "../Sidebar";
import { Notes } from "../Notes";
import { useDeleteNodes } from "../../hooks/useDeleteNodes";
import { useAddPaths } from "../../hooks/useAddPaths";
import useUpdateNode from "../../hooks/useUpdateNode";
import { useUpdateEdge } from "../../hooks/useUpdateEdge";

import styles from "./PathSidebar.module.css";

export function PathSidebar({ id }: { id: string }) {
  const node = useAtomValue(nodeAtomFamily(id)) as Node<PathNodeData>;
  const { onNodesDelete } = useDeleteNodes();
  const nodes = useAtomValue(nodesAtom);
  const edges = useAtomValue(edgesAtom);
  const childrenEdges = useAtomValue(childrenEdgesAtomFamily(id));
  const addPath = useAddPaths(id, 1, false);
  const updateNodeAndSave = useUpdateNode();
  const updateEdge = useUpdateEdge();
  const saveToBackend = useSetAtom(saveCanvasSetter);

  const onDeleteStep = useCallback(() => {
    if (node) {
      onNodesDelete([node]);
    }
  }, [onNodesDelete, node]);

  const decisionInputRef = useRef<HTMLInputElement>(null);

  // @ts-ignore I swear label is on NodeData
  const label = node?.data?.label;

  const onChangeLabel = useCallback(
    (evt: React.ChangeEvent<HTMLTextAreaElement>) => {
      // Disregard var name: we don't want to spam the BE on each keydown, so we update the node without saving
      updateNodeAndSave(
        {
          id: node.id,
          data: {
            label: evt.target.value,
          },
        },
        { shouldSave: false },
      );
    },
    [updateNodeAndSave, node.id],
  );

  const onChangeEdgeLabel = useCallback(
    (label: string, edgeId: string) => {
      updateEdge({
        id: edgeId,
        label,
      });
    },
    [updateEdge],
  );

  const originalLabelRef = useRef(label);

  const saveTitle = useCallback(async () => {
    const hasTitleChanged = originalLabelRef.current !== label;
    if (hasTitleChanged) {
      saveToBackend({ nodes });

      // Update the original label reference after saving
      originalLabelRef.current = label;
    }
  }, [label, nodes, saveToBackend]);

  // Ref to keep track of the original labels of all edges
  const originalEdgeLabelsRef = useRef(
    childrenEdges.reduce<{ [key: string]: string }>((labels, edge) => {
      labels[edge.id] = edge.label ? String(edge.label) : "";
      return labels;
    }, {}),
  );

  const saveEdges = useCallback(
    async (edge: Edge) => {
      const edgeId = edge.id;

      const hasEdgeLabelChanged =
        originalEdgeLabelsRef.current[edgeId] !== edge.label;

      if (hasEdgeLabelChanged) {
        saveToBackend({ edges });

        // Update the original edge label reference after saving
        originalEdgeLabelsRef.current[edgeId] = edge.label
          ? String(edge.label)
          : "";
      }
    },
    [edges, saveToBackend],
  );

  return (
    <Sidebar heading="Path details" onDeleteStep={onDeleteStep}>
      <div className={styles.sectionWrapper}>
        <div className={styles.fieldWrapper}>
          <Text type="SmallPrint2">Title</Text>
          <TextInput
            ref={decisionInputRef}
            aria-required={true}
            placeholder="Describe the conditional for this step"
            value={label}
            onChange={onChangeLabel}
            onBlur={saveTitle}
          />
        </div>
        <div>
          <Text type="SmallPrint2">Paths</Text>
          <Spacer height={5} />
          <ul className={styles.pathList}>
            {childrenEdges.map((edge) => (
              <li key={edge.id}>
                <TextInput
                  key={edge.id}
                  placeholder="Step label"
                  value={edge.label as string}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    onChangeEdgeLabel(e.target.value, edge.id)
                  }
                  onBlur={() => saveEdges(edge)}
                />
              </li>
            ))}
          </ul>
          <Spacer height={10} />
          <span>
            <Button
              size="compact"
              onClick={() => addPath()}
              color="secondary"
              iconBefore="formAdd"
            >
              Add path
            </Button>
          </span>
        </div>
        <Notes nodeId={node.id} notesJson={node.data.notesJson} />
      </div>
    </Sidebar>
  );
}
